import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { reportClientError } from "@/lib/report-client-error";

interface Props {
  children: React.ReactNode;
  /** Optional fallback render. Receives the error and a reset fn. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /** Where this boundary lives - included in console + telemetry tags. */
  scope?: string;
}

interface State {
  error: Error | null;
}

const RELOAD_FLAG = "fc:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * After a new version is deployed, an open tab may still reference old
 * JS files that no longer exist, producing "Failed to fetch dynamically
 * imported module". A single self-reload pulls the fresh version.
 */
function isStaleChunkError(error: unknown): boolean {
  const msg = (error as Error)?.message ?? "";
  return (
    /dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

function tryReloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* sessionStorage unavailable - still attempt one reload */
  }
  window.location.reload();
  return true;
}

/**
 * Generic error boundary. Wrap the whole app once at the root, and again
 * around every lazy route so a single render-time throw doesn't blank the
 * entire app. Never rethrows in production.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }


  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isStaleChunkError(error) && tryReloadOnce()) return;
    const scope = this.props.scope;

    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${scope ? ":" + scope : ""}]`,
      error,
      info.componentStack
    );
    // Fire-and-forget telemetry. Never throw from here.
    try {
      void reportClientError({
        scope,
        message: error?.message ?? "Unknown error",
        stack: error?.stack ?? null,
        componentStack: info.componentStack ?? null,
      });
    } catch {
      /* swallow */
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="rounded-full bg-destructive/10 p-3">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">Something went wrong</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            We hit an unexpected error rendering this page. You can try again,
            or head back to the dashboard.
          </p>
        </div>
        {import.meta.env.DEV && (
          <pre className="max-w-2xl overflow-auto rounded bg-muted p-3 text-left text-xs text-muted-foreground">
            {error.message}
          </pre>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={this.reset}>
            Try again
          </Button>
          <Button onClick={() => (window.location.href = "/")}>Go home</Button>
        </div>
      </div>
    );
  }
}

/** Convenience wrapper for routes. */
export function RouteErrorBoundary({
  children,
  scope,
}: {
  children: React.ReactNode;
  scope?: string;
}) {
  return <ErrorBoundary scope={scope}>{children}</ErrorBoundary>;
}
